import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Button from "../../components/Button";
import Footer from "../Layout/Footer";
import {
  EyeIcon,
  EyeSlashIcon,
  UserIcon,
  LockClosedIcon,
  IdentificationIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";

const Register = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
    reset,
  } = useForm();

  const onSubmit = async (data) => {
    if (data.password.length < 8) {
      setError("password", {
        type: "manual",
        message: "Password must be at least 8 characters long",
      });
      return;
    }
    if (data.password !== data.password_confirmation) {
      setError("password_confirmation", {
        type: "manual",
        message: "Passwords do not match",
      });
      return;
    }
    try {
      await axios.post(window.REACT_APP_API_ENDPOINT + "/register", data);
      toast.success("Registration successful! Please log in.");
      reset();
      navigate("/login");
    } catch (error) {
      toast.error("Registration failed. Please try again.");
    }
  };

  return (
    <>
      <div className="flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8 bg-bgBlue">
        <div className="flex lg:flex-row flex-col mt-10 sm:mx-auto sm:w-full sm:max-w-[1200px] mb-20">
          <div className="w-full lg:w-1/2 relative flex flex-col justify-center items-center px-6 py-12 shadow sm:rounded-lg sm:px-12 bg-gradient-to-br from-blue-800 via-bluePrimary to-blue-400 mr-4 mb-4 lg:mb-0 overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none select-none">
              <svg width="100%" height="100%">
                <circle cx="80%" cy="20%" r="120" fill="#fff" />
              </svg>
            </div>
            <div className="relative z-10 flex flex-col items-center w-full">
              <div className="w-16 h-1 bg-white rounded-full mb-6" />
              <h2 className="text-white text-4xl font-extrabold mb-6 drop-shadow-lg tracking-tight text-center">
                RitexApp
              </h2>
              <h3 className="text-white text-5xl font-extrabold mb-4 text-center leading-tight drop-shadow-lg">
                Start your journey with us
              </h3>
              <p className="text-blue-100 text-lg font-medium text-center mb-2">
                Your trusted companion for print ordering...
              </p>
              <span className="inline-block mt-4 px-4 py-2 bg-white/20 text-white rounded-full font-semibold text-sm tracking-wide shadow">
                Fast • Reliable • Secure
              </span>
            </div>
          </div>
          <div className="w-full lg:w-1/2 bg-white px-6 py-12 shadow sm:rounded-lg sm:px-12">
            <h1 className="font-bold text-4xl mb-4">Sign Up</h1>
            <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
              <div className="mb-2">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <IdentificationIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="name"
                    {...register("name", {
                      required: "First Name is required",
                    })}
                    type="text"
                    placeholder="First Name"
                    className={`pl-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      errors.name ? "ring-red-500" : ""
                    }`}
                  />
                  {errors.name && (
                    <span className="text-sm text-red-600">
                      {errors.name.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <IdentificationIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="last_name"
                    {...register("last_name", {
                      required: "Last Name is required",
                    })}
                    type="text"
                    placeholder="Last Name"
                    autoComplete="last_name"
                    className={`pl-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      errors.last_name ? "ring-red-500" : ""
                    }`}
                  />
                  {errors.last_name && (
                    <span className="text-sm text-red-600">
                      {errors.last_name.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <UserIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="username"
                    {...register("username", {
                      required: "Username is required",
                    })}
                    type="text"
                    placeholder="Username"
                    autoComplete="username"
                    className={`pl-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      errors.username ? "ring-red-500" : ""
                    }`}
                  />
                  {errors.username && (
                    <span className="text-sm text-red-600">
                      {errors.username.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <select
                  id="role"
                  {...register("role", { required: "Role is required" })}
                  className={`mt-2 block w-full rounded-md border-0 py-3 pl-3 pr-10 text-gray-900 ring-1 ring-inset ring-gray-500 focus:ring-2 focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                    errors.role ? "ring-red-500" : ""
                  }`}
                  defaultValue=""
                >
                  <option className="text-gray-900" value="" disabled>
                    Role
                  </option>
                  <option className="text-gray-900" value={"Stock Manager"}>
                    Stock Manager
                  </option>
                  <option className="text-gray-900" value={"Technician"}>
                    Technician
                  </option>
                </select>
                {errors.role && (
                  <span className="text-sm text-red-600">
                    {errors.role.message}
                  </span>
                )}
              </div>
              <div className="mb-2">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <LockClosedIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="password"
                    {...register("password", {
                      required: "Password is required",
                    })}
                    type={showPassword ? "text" : "password"}
                    placeholder="Password"
                    autoComplete="current-password"
                    className={`pl-10 pr-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      errors.password ? "ring-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    onClick={() => setShowPassword((v) => !v)}
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                  {errors.password && (
                    <span className="text-sm text-red-600">
                      {errors.password.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="mb-2">
                <div className="mt-2 relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
                    <LockClosedIcon className="h-5 w-5" />
                  </span>
                  <input
                    id="password_confirmation"
                    {...register("password_confirmation", {
                      required: "Confirm Password is required",
                    })}
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="Confirm Password"
                    autoComplete="current-password"
                    className={`pl-10 pr-10 block w-full rounded-md border-0 py-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-500 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 ${
                      errors.password_confirmation ? "ring-red-500" : ""
                    }`}
                  />
                  <button
                    type="button"
                    tabIndex={-1}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                    onClick={() => setShowConfirmPassword((v) => !v)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                  {errors.password_confirmation && (
                    <span className="text-sm text-red-600">
                      {errors.password_confirmation.message}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                  />
                  <label
                    htmlFor="remember-me"
                    className="ml-3 block text-sm leading-6 text-gray-900"
                  >
                    I agree to the terms and conditions
                  </label>
                </div>
                <div className="text-sm leading-6">
                  <a
                    href="#"
                    className="font-semibold text-indigo-600 hover:text-indigo-500"
                  >
                    Forgot password?
                  </a>
                </div>
              </div>
              <div>
                <Button
                  type="submit"
                  className="uppercase flex w-full justify-center rounded-md bg-bluePrimary px-3 py-3 text-sm font-bold leading-6 text-white shadow-sm hover:bg-bluePrimary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  Create an account
                </Button>
              </div>
            </form>
            <p className="mt-10 text-center text-sm text-black">
              Already have an account?{" "}
              <a
                href="/login"
                className="font-semibold leading-6 text-indigo-600 hover:text-indigo-500"
              >
                Log In
              </a>
            </p>
          </div>
        </div>
        <Footer />
      </div>
    </>
  );
};

export default Register;
